import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Building2, MapPin, ExternalLink, Home, Star, DollarSign, Filter, Calendar } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardHeader, CardTitle, CardDescription, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import TourRequestModal from '@/components/TourRequestModal';

const NewConstruction = () => {
  const [filter, setFilter] = useState('all'); // 'all', 'luxury', 'standard'
  const [showTourModal, setShowTourModal] = useState(false);
  const [selectedBuilder, setSelectedBuilder] = useState('');
  const [selectedCommunity, setSelectedCommunity] = useState('');

  const openTourModal = (builderName = '', communityName = '') => {
    setSelectedBuilder(builderName);
    setSelectedCommunity(communityName);
    setShowTourModal(true);
  };

  const builders = [
    {
      id: 'regency',
      name: 'Regency Homes',
      website: 'https://newregencyhomes.com/',
      isLuxury: true,
      priceRange: 'High $300s - High $700s',
      highlightedCommunity: {
        name: 'Hawks Crossing',
        location: 'Olive Branch, MS',
        link: 'https://newregencyhomes.com/olive-branch-2',
        description: `Regency Homes at Hawks Crossing brings elevated living to one of Olive Branch's most sought-after communities. With prices starting around $624,900, these homes deliver thoughtful design, premium finishes, and a lifestyle that feels both refined and relaxed.`,
        features: [
          'Smart, stylish living with timeless architecture',
          'Prime location near top-rated schools & shopping',
          'Perfect for growing families, professionals & empty nesters',
          'Luxury without being over the top'
        ]
      },
      communities: [
        { name: 'Arlington', link: 'https://newregencyhomes.com/arlington' },
        { name: 'Olive Branch', link: 'https://newregencyhomes.com/olive-branch-2' },
        { name: 'Collierville', link: 'https://newregencyhomes.com/collierville' },
        { name: 'Cordova', link: 'https://newregencyhomes.com/cordova' },
        { name: 'Eads', link: 'https://newregencyhomes.com/eads' },
        { name: 'Nesbit', link: 'https://newregencyhomes.com/nesbit' },
        { name: 'Germantown', link: 'https://newregencyhomes.com/germantown' },
        { name: 'Piperton', link: 'https://newregencyhomes.com/piperton' },
        { name: 'Rossville', link: 'https://newregencyhomes.com/rossville' }
      ]
    },
    {
      id: 'magnolia',
      name: 'Magnolia Homes',
      website: 'https://www.yourmagnoliahome.com/',
      isLuxury: true,
      priceRange: '$700,000 - $1.7 Million',
      highlightedCommunity: {
        name: 'Shepherds Creek',
        location: 'Memphis Area',
        link: 'https://www.yourmagnoliahome.com/community/shepherds-creek/',
        description: `Shepherds Creek represents refined luxury living at its best. Built by Magnolia Homes, this exclusive community is known for exceptional craftsmanship, architectural detail, and homes designed for buyers who expect excellence—inside and out.`,
        features: [
          'Estate-style living with modern sophistication',
          'Custom-level finishes & premium materials',
          'Expansive layouts with exceptional privacy',
          'Perfect for luxury buyers & executives'
        ]
      },
      communities: [
        { name: 'Shepherds Creek', link: 'https://www.yourmagnoliahome.com/community/shepherds-creek/' },
        { name: 'Cypress Grove', link: 'https://www.yourmagnoliahome.com/community/cypress-grove/' },
        { name: 'Oak Hill', link: 'https://www.yourmagnoliahome.com/community/oak-hill/' },
        { name: 'Villages of Saunders Creek', link: 'https://www.yourmagnoliahome.com/community/villages-of-saunders-creek/' }
      ]
    },
    {
      id: 'grant',
      name: 'Grant & Co',
      website: 'https://www.grantnewhomes.com/',
      isLuxury: true,
      priceRange: 'Mid $300s - High $700s',
      highlightedCommunity: {
        name: 'Amherst',
        location: 'Southeast Shelby County, TN',
        link: 'https://www.grantnewhomes.com/communities/collierville-tn',
        description: `Amherst brings exceptional value, thoughtful design, and smart financing options together in a community that feels like home from day one. Built by Grant Homes, these new construction homes deliver quality, style, and livability.`,
        features: [
          '100% financing available through THDA Great Choice Program',
          '3% Buyer\'s Bonus toward closing costs',
          '$0 move-in opportunity for first-time homebuyers',
          '10 minutes from Collierville, near Shelby Farms'
        ],
        specialProgram: '100% Financing Available'
      },
      communities: [
        { name: 'Arlington', link: 'https://www.grantnewhomes.com/homes/arlington-tn' },
        { name: 'Bartlett', link: 'https://www.grantnewhomes.com/communities/bartlett-tn' },
        { name: 'Collierville', link: 'https://www.grantnewhomes.com/communities/collierville-tn' },
        { name: 'Cordova', link: 'https://www.grantnewhomes.com/communities/cordova-tn' },
        { name: 'Oakland', link: 'https://www.grantnewhomes.com/communities/oakland-tn' },
        { name: 'Munford', link: 'https://www.grantnewhomes.com/communities/munford-tn' },
        { name: 'Olive Branch', link: 'https://www.grantnewhomes.com/communities/olive-branch-ms' }
      ]
    },
    {
      id: 'legacy',
      name: 'Legacy Homes',
      website: 'https://www.legacynewhomes.cc/',
      isLuxury: false,
      priceRange: 'High $300s - Low $500s',
      highlightedCommunity: {
        name: 'Winningham Estates',
        location: 'Hernando, MS',
        link: 'https://www.legacynewhomes.cc/communities/hernando-mississippi',
        description: `Winningham Estates is the definition of timeless, well-planned living. Built by Legacy Homes, this community stands out for its craftsmanship, thoughtful layouts, and the kind of neighborhood feel that never goes out of style.`,
        features: [
          'Classic homes that age beautifully',
          'Strong location with long-term value',
          'Perfect for families & move-up buyers',
          'Pride of ownership in every detail'
        ]
      },
      communities: [
        { name: 'Hernando', link: 'https://www.legacynewhomes.cc/communities/hernando-mississippi' },
        { name: 'Lake Cormorant', link: 'https://www.legacynewhomes.cc/communities/lake-cormorant-mississippi' },
        { name: 'Southaven', link: 'https://www.legacynewhomes.cc/communities/southaven-mississippi' }
      ]
    },
    {
      id: 'skylake',
      name: 'Sky Lake Construction',
      website: 'https://www.skylakeconstruction.com/',
      isLuxury: false,
      priceRange: 'Low $300s - Mid $400s',
      highlightedCommunity: {
        name: 'Center Hill Farms',
        location: 'Memphis Area',
        link: 'https://www.skylakeconstruction.com/centerhillfarm',
        description: `Center Hill Farms offers flexibility, variety, and thoughtful design in a community built for real life. Developed by Sky Lake Construction, this neighborhood stands out for giving buyers real choices—without sacrificing quality or style.`,
        features: [
          '26 thoughtfully designed floor plans',
          'Options for every lifestyle and family size',
          'Modern layouts with personality',
          'Value and variety in one community'
        ],
        specialProgram: '26 Floor Plans Available'
      },
      communities: [
        { name: 'Center Hill Farms', link: 'https://www.skylakeconstruction.com/centerhillfarm' },
        { name: 'Creekside Meadow', link: 'https://www.skylakeconstruction.com/creekside-meadow-1' },
        { name: 'Cross Winds', link: 'https://www.skylakeconstruction.com/cross-winds' },
        { name: 'Delta Landing', link: 'https://delta-landing.com/' },
        { name: 'Long Branch', link: 'https://www.skylakeconstruction.com/long-branch-1' }
      ]
    },
    {
      id: 'drhorton',
      name: 'DR Horton',
      website: 'https://www.drhorton.com/',
      isLuxury: false,
      priceRange: 'High $200s - Low $300s',
      highlightedCommunity: {
        name: 'Villages of Southbranch',
        location: 'Olive Branch, MS',
        link: 'https://www.drhorton.com/mississippi/north-mississippi/olive-branch/villages-at-southbranch',
        description: `Villages of Southbranch offers attainable new construction paired with the reliability and efficiency DR Horton is known for. This community is ideal for buyers seeking comfort, functionality, and value in a thoughtfully planned neighborhood.`,
        features: [
          'Excellent entry point for new construction',
          'Practical, well-designed floor plans',
          'Perfect for first-time homebuyers',
          'Trusted construction & move-in ready'
        ],
        specialProgram: 'First-Time Buyer Friendly'
      },
      communities: [
        { name: 'Villages of Southbranch', link: 'https://www.drhorton.com/mississippi/north-mississippi/olive-branch/villages-at-southbranch' },
        { name: 'Indian Oaks', link: 'https://www.drhorton.com/mississippi/north-mississippi/byhalia/indian-oaks' },
        { name: 'Chickasaw Trails', link: 'https://www.drhorton.com/mississippi/north-mississippi/byhalia/chickasaw-trails' },
        { name: 'Cayce Pointe', link: 'https://www.drhorton.com/mississippi/north-mississippi/byhalia/cayce-pointe' },
        { name: 'Twin Lakes', link: 'https://www.drhorton.com/mississippi/north-mississippi/byhalia/twin-lakes' },
        { name: 'Riggs Pointe', link: 'https://www.drhorton.com/tennessee/memphis/millington/riggs-pointe' },
        { name: 'The Orchard', link: 'https://www.drhorton.com/tennessee/memphis/millington/the-orchard' },
        { name: 'Huntington Estates', link: 'https://www.drhorton.com/tennessee/memphis/millington/huntington-estates' },
        { name: 'Riverwood Gardens', link: 'https://www.drhorton.com/tennessee/memphis/oakland/riverwood-gardens' },
        { name: 'Village Park', link: 'https://www.drhorton.com/tennessee/memphis/oakland/village-park' },
        { name: 'Hedge Rose', link: 'https://www.drhorton.com/tennessee/memphis/somerville/hedge-rose' }
      ]
    }
  ];

  const filteredBuilders = builders.filter(builder => {
    if (filter === 'all') return true;
    if (filter === 'luxury') return builder.isLuxury;
    if (filter === 'standard') return !builder.isLuxury;
    return true;
  });

  return (
    <div className="min-h-screen" data-testid="new-construction-page">
      {/* Hero Section */}
      <section className="relative h-[50vh] flex items-center justify-center overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-r from-black/70 to-black/50 z-10"></div>
        <div 
          className="absolute inset-0 bg-cover bg-center"
          style={{
            backgroundImage: 'url(https://customer-assets.emergentagent.com/job_eduhub-78/artifacts/metqdwkt_OliveBranchRealEstate.jpg)',
          }}
        ></div>
        <div className="relative z-20 text-center text-white px-4 max-w-4xl mx-auto">
          <Building2 className="h-16 w-16 mx-auto mb-6 text-[#BCA480]" />
          <h1 className="text-4xl md:text-5xl font-bold mb-4 text-white" data-testid="page-heading">New Construction</h1>
          <p className="text-xl text-white/90 mb-6">
            Discover brand new homes from trusted builders across Memphis, Tennessee, Mississippi &amp; Arkansas
          </p>
          <Button 
            onClick={() => openTourModal()}
            size="lg"
            className="bg-[#BCA480] hover:bg-[#a89470] text-white"
            data-testid="hero-schedule-tour-btn"
          >
            <Calendar className="h-5 w-5 mr-2" />
            Schedule a Tour
          </Button>
        </div>
      </section>

      {/* Intro Section */}
      <section className="py-12 bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <p className="text-lg text-gray-700 leading-relaxed">
            Whether you're a first-time buyer looking for value, a growing family seeking space, or a luxury buyer 
            ready for your dream home—we've partnered with the area's most trusted builders to help you find 
            the perfect new construction home. Let us guide you through the process and help you discover 
            communities you'll love.
          </p>
        </div>
      </section>

      {/* Filter Section */}
      <section className="py-6 bg-gray-50 border-y">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-wrap items-center justify-center gap-3">
            <Filter className="h-5 w-5 text-gray-500" />
            <span className="text-gray-600 font-medium mr-2">Filter by:</span>
            <Button
              variant={filter === 'all' ? 'default' : 'outline'}
              size="sm"
              onClick={() => setFilter('all')}
              className={filter === 'all' ? 'bg-[#BCA480] hover:bg-[#a89470]' : ''}
              data-testid="filter-all"
            >
              All Builders
            </Button>
            <Button
              variant={filter === 'luxury' ? 'default' : 'outline'}
              size="sm"
              onClick={() => setFilter('luxury')}
              className={filter === 'luxury' ? 'bg-[#BCA480] hover:bg-[#a89470]' : ''}
              data-testid="filter-luxury"
            >
              <Star className="h-4 w-4 mr-1" />
              Luxury
            </Button>
            <Button
              variant={filter === 'standard' ? 'default' : 'outline'}
              size="sm"
              onClick={() => setFilter('standard')}
              className={filter === 'standard' ? 'bg-[#BCA480] hover:bg-[#a89470]' : ''}
              data-testid="filter-standard"
            >
              <Home className="h-4 w-4 mr-1" />
              Value & First-Time Buyer
            </Button>
          </div>
        </div>
      </section>

      {/* Builders Section */}
      <section className="py-16 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="space-y-12">
            {filteredBuilders.map((builder) => (
              <Card key={builder.id} className="overflow-hidden hover:shadow-xl transition-shadow" data-testid={`builder-${builder.id}`}>
                {/* Builder Header */}
                <div className={`p-6 ${builder.isLuxury ? 'bg-gradient-to-r from-[#434345] to-[#5a5a5c]' : 'bg-gradient-to-r from-[#BCA480] to-[#a89470]'} text-white`}>
                  <div className="flex flex-wrap items-center justify-between gap-4">
                    <div>
                      <div className="flex items-center gap-3">
                        <h2 className="text-2xl md:text-3xl font-bold">{builder.name}</h2>
                        {builder.isLuxury && (
                          <Badge className="bg-white/20 text-white border-white/30">
                            <Star className="h-3 w-3 mr-1 fill-current" />
                            Luxury
                          </Badge>
                        )}
                      </div>
                      <div className="flex items-center gap-2 mt-2 text-white/90">
                        <DollarSign className="h-4 w-4" />
                        <span>{builder.priceRange}</span>
                      </div>
                    </div>
                    <Button
                      asChild
                      variant="outline"
                      className="border-white text-white hover:bg-white hover:text-[#434345]"
                    >
                      <a href={builder.website} target="_blank" rel="noopener noreferrer">
                        Visit Builder Site
                        <ExternalLink className="h-4 w-4 ml-2" />
                      </a>
                    </Button>
                  </div>
                </div>

                <CardContent className="p-6">
                  <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                    {/* Highlighted Community */}
                    <div className="bg-gradient-to-br from-[#BCA480]/5 to-[#BCA480]/10 p-6 rounded-lg border border-[#BCA480]/20">
                      <div className="flex items-center gap-2 mb-3">
                        <Star className="h-5 w-5 text-[#BCA480] fill-[#BCA480]" />
                        <span className="text-sm font-semibold text-[#BCA480] uppercase tracking-wide">Featured Community</span>
                      </div>
                      <h3 className="text-xl font-bold text-[#434345] mb-1">{builder.highlightedCommunity.name}</h3>
                      <div className="flex items-center text-gray-600 mb-3">
                        <MapPin className="h-4 w-4 mr-1" />
                        <span className="text-sm">{builder.highlightedCommunity.location}</span>
                      </div>
                      
                      {builder.highlightedCommunity.specialProgram && (
                        <Badge className="mb-3 bg-green-100 text-green-800 border-green-200">
                          {builder.highlightedCommunity.specialProgram}
                        </Badge>
                      )}
                      
                      <p className="text-gray-700 text-sm mb-4 leading-relaxed">
                        {builder.highlightedCommunity.description}
                      </p>
                      
                      <div className="space-y-2 mb-4">
                        {builder.highlightedCommunity.features.map((feature, idx) => (
                          <div key={idx} className="flex items-start gap-2 text-sm text-gray-600">
                            <span className="text-[#BCA480] mt-0.5">✓</span>
                            <span>{feature}</span>
                          </div>
                        ))}
                      </div>
                      
                      <Button
                        asChild
                        className="w-full bg-[#BCA480] hover:bg-[#a89470] text-white"
                      >
                        <a href={builder.highlightedCommunity.link} target="_blank" rel="noopener noreferrer">
                          Explore {builder.highlightedCommunity.name}
                          <ExternalLink className="h-4 w-4 ml-2" />
                        </a>
                      </Button>
                    </div>

                    {/* Other Communities */}
                    <div>
                      <h4 className="font-semibold text-[#434345] mb-4">All Communities</h4>
                      <div className="grid grid-cols-2 gap-2">
                        {builder.communities.map((community, idx) => (
                          <a
                            key={idx}
                            href={community.link}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="flex items-center gap-2 p-3 rounded-lg border hover:border-[#BCA480] hover:bg-[#BCA480]/5 transition-all group"
                          >
                            <Home className="h-4 w-4 text-gray-400 group-hover:text-[#BCA480]" />
                            <span className="text-sm text-gray-700 group-hover:text-[#BCA480]">{community.name}</span>
                          </a>
                        ))}
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <div className="bg-gradient-to-br from-[#434345] to-[#5a5a5c] p-8 md:p-12 rounded-2xl text-white">
            <h2 className="text-2xl md:text-3xl font-bold mb-4">Ready to Find Your New Home?</h2>
            <p className="text-white/90 mb-6 max-w-2xl mx-auto">
              Our team specializes in new construction and can help you navigate builder incentives, 
              financing options, and find the perfect community for your lifestyle.
            </p>
            <div className="flex flex-wrap justify-center gap-4">
              <Button
                asChild
                size="lg"
                className="bg-[#BCA480] hover:bg-[#a89470] text-white"
              >
                <Link to="/contact">
                  Schedule a Consultation
                </Link>
              </Button>
              <Button
                asChild
                size="lg"
                variant="outline"
                className="border-white text-white hover:bg-white hover:text-[#434345]"
              >
                <Link to="/mls">
                  Browse All Homes
                </Link>
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* Disclaimer */}
      <section className="py-8 bg-gray-100">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <p className="text-xs text-gray-500 text-center">
            *Home prices, features, plans, incentives, and availability are subject to change without notice. 
            Please contact a Bespoke Realty representative for the most current information. 
            Eligibility requirements may apply for special financing programs.
          </p>
        </div>
      </section>

      {/* Tour Request Modal */}
      <TourRequestModal
        isOpen={showTourModal}
        onClose={() => setShowTourModal(false)}
        builderName={selectedBuilder}
        communityName={selectedCommunity}
        builders={builders}
      />
    </div>
  );
};

export default NewConstruction;
